import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  useTheme,
  useMediaQuery,
  Button,
  TableCell,
  TableRow,
  Typography,
  Menu,
  MenuItem,
  IconButton,
  Checkbox,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import CommonPagination from "../common-components/CommonPagination";
import { SearchIcon } from "../../assets/Icon/ArrowIcon";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CloseIcon from "@mui/icons-material/Close";
import {
  deleteCustomInvoice,
  deleteInvoice,
  getCustomInvoices,
  getDashboardData,
  getInvoiceList,
  sendInvoice,
  sendStripeCode,
} from "../../stores/clients";
import Modal from "@mui/material/Modal";
import {
  ModalWrapper,
  CloseButton,
  CloseButtonWrapper,
} from "../style/modalStyle";
import { useDispatch } from "react-redux";
import { FIlterIcon, TrashIconForPopup } from "../../assets/Icon/headerCicon";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setToast } from "../../stores/toast/toastslice";
import CustomTable from "../common-components/CustomTable";
import CircularProgress from "@mui/material/CircularProgress";
import { formatDate } from "../../utils/functions";
import { CUSTOM_OPTIONS, OPTIONS } from "../../utils/constants";
import { CrossIcon, ModalBottom, ModalTop } from "../../assets/Icon/ModalIcon";

const CustomInvoiceTable = ({ type }: { type: string }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.between("md", "lg"));

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [openFilter, setOpenFilter] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");

  const filterRef = useRef<HTMLDivElement>(null);

  const { total_records } = useSelector((state: any) => state.clients);

  const searchDebounceRef = useRef<NodeJS.Timeout | null>(null);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [deleting, setDeleting] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [pageNo, setPageNo] = useState(1);
  const [limit] = useState(10);
  const [searchLoading, setSearchLoading] = useState(false);
  const [paginationLoading, setPaginationLoading] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(
    null
  );
  const [openMenuInvoiceId, setOpenMenuInvoiceId] = useState<string | null>(
    null
  );
  const [appliedFilterValues, setAppliedFilterValues] = useState<number[]>([]);
  const [tempFilterValues, setTempFilterValues] = useState<number[]>([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [customData, setCustomData] = useState<any>(null);
  const [stripeConnecting, setStripeConnecting] = useState(false);

  const handleOpen = (
    event: React.MouseEvent<HTMLElement>,
    invoiceId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedInvoiceId(invoiceId);
    setOpenMenuInvoiceId(invoiceId);
  };

  const handleClose = (option?: string) => {
    setAnchorEl(null);
    if (option && selectedInvoiceId) {
      handleOptions(option, selectedInvoiceId);
    }
    setSelectedInvoiceId(null);
    setOpenMenuInvoiceId(null);
  };

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    setSearchLoading(true);
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      dispatch<any>(
        getCustomInvoices({
          offset: 0,
          limit: 10,
          search: value,
        })
      )
        .finally(() => setSearchLoading(false))
        .then((result: any) => {
          setCustomData(result?.payload?.data);
        });
    }, 1500);
  };

  const handlePageChange = (newPage: number) => {
    setPageNo(newPage);
  };

  const fetchAgents = () => {
    const offset = (pageNo - 1) * limit;
    setPaginationLoading(true);

    dispatch<any>(
      getCustomInvoices({
        offset: offset,
        limit: 10,
        search: searchValue,
      })
    )
      .finally(() => {
        setPaginationLoading(false);
      })
      .then((result: any) => {
        setCustomData(result?.payload?.data);
      });
  };

  useEffect(() => {
    fetchAgents();
  }, [pageNo]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Close filter dropdown
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target as Node)
      ) {
        setOpenFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    // Extract data from the URL after STRIPE success
    const params = new URLSearchParams(window.location.search);
    const code = params.get("code");
    const state = params.get("state");

    if (code) {
      setStripeConnecting(true);
      dispatch<any>(sendStripeCode({ code, state })).finally(() =>
        setStripeConnecting(false)
      );
    }
  }, [dispatch]);

  const handleOptions = (option: string, id: string) => {
    const invoice = customData?.find(
      (inv: any) => String(inv.id) === String(id)
    );
    if (option === "View") {
      navigate(`/view-custom-invoice/${id}`);
    } else if (option === "Edit") {
      navigate("/tools/invoices/custom-invoice-form", {
        state: { invoice_id: id, viewOnly: false },
      });
    } else if (option === "Copy URL") {
      const baseUrl = window.location.origin;
      const url = `${baseUrl}/view-custom-invoice/${id}`;

      navigator.clipboard.writeText(url).then(() => {
        dispatch<any>(
          setToast({
            message: "Invoice link copied to clipboard!",
            type: "success",
            title: "Copied",
            onClose: () => {},
          })
        );
      });
    } else if (option === "Send") {
      setSending(true);
      dispatch<any>(sendInvoice({ invoice_id: Number(id) })).then(
        (result: any) => {
          setSending(false);
          if (result?.payload?.code === 200) {
            dispatch<any>(
              setToast({
                message:
                  result?.payload?.message || "Invoice sent successfully!",
                type: "success",
                title: "Success",
                onClose: () => {},
              })
            );
          } else {
            dispatch<any>(
              setToast({
                message: result?.payload?.message || "Failed to send invoice!",
                type: "error",
                title: "Error",
                onClose: () => {},
              })
            );
          }
        }
      );
    } else if (option === "Delete") {
      setPendingDeleteId(id);
      setDeleteModalOpen(true);
    }
  };

  const handleDeleteConfirm = () => {
    if (!pendingDeleteId) return;
    setDeleting(true);
    setDeleteModalOpen(false);
    dispatch<any>(deleteCustomInvoice(pendingDeleteId?.toString())).then(
      (result: any) => {
        dispatch<any>(
          getCustomInvoices({
            offset: 0,
            limit: 10,
            search: searchValue,
          })
        ).then((res: any) => {
          setDeleting(false);
          setCustomData(res?.payload?.data);
          if (result?.payload.code === 200) {
            dispatch<any>(
              setToast({
                message: result?.payload?.message,
                type: "success",
                title: "Success",
                onClose: () => {},
              })
            );
          } else if (result?.payload?.code === 400) {
            dispatch<any>(
              setToast({
                message:
                  result?.payload?.message || "Failed to delete invoice!",
                type: "error",
                title: "Error",
                onClose: () => {},
              })
            );
          }
          dispatch<any>(getDashboardData({ type: type })).then(
            (result: any) => {
              if (result?.payload?.data) setDashboardData(result.payload?.data);
            }
          );
        });
      }
    );
    setPendingDeleteId(null);
  };

  const handleDeleteCancel = () => {
    setDeleteModalOpen(false);
    setPendingDeleteId(null);
  };

  return (
    <div>
      <div
        style={{
          display: isMobile ? "grid" : "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "white",
          padding: 20,
          marginTop: 5,
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
        }}
      >
        <div
          style={{
            fontSize: "20px",
            fontWeight: 600,
          }}
        >
          Custom Invoicing
        </div>
        <div
          style={{
            display: isMobile ? "grid" : "flex",
            alignItems: "center",
            gap: 20,
          }}
        >
          <div
            style={{
              backgroundColor: "#F6F9FD",
              height: "40px",
              display: "flex",
              alignItems: "center",
              paddingLeft: 10,
              borderRadius: "8px",
              width: isMobile || isTablet ? "250px" : "318px",
              marginTop: isMobile ? 10 : 0,
              position: "relative",
            }}
          >
            <SearchIcon />
            <input
              style={{
                border: "none",
                backgroundColor: "transparent",
                padding: 3,
                outline: "none",
                paddingLeft: 10,
                width: "82%",
              }}
              value={searchValue}
              onChange={handleSearchInput}
              placeholder="Search here..."
            />
            {searchValue && (
              <CloseIcon
                onClick={() => {
                  setSearchValue("");
                  dispatch<any>(
                    getCustomInvoices({
                      offset: 0,
                      limit: 10,
                      search: "",
                    })
                  ).then((result: any) => {
                    setCustomData(result?.payload?.data);
                  });
                }}
                style={{
                  position: "absolute",
                  right: 8,
                  cursor: "pointer",
                  color: "#97999B",
                  fontSize: 20,
                }}
              />
            )}
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <div ref={filterRef} style={{ position: "relative" }}>
              {/* Red dot indicator when filter is applied */}
              {appliedFilterValues.length > 0 && (
                <span
                  style={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    width: 10,
                    height: 10,
                    background: "#F44336",
                    borderRadius: "50%",
                    zIndex: 2,
                    border: "2px solid #fff",
                  }}
                />
              )}
              {/* <Button
                style={{
                  border: "1px solid #EAF1FA",
                  borderRadius: "8px",
                  height: "40px",
                  padding: 15,
                  color: "#6C6F71",
                  fontSize: "12px",
                  fontWeight: 600,
                  gap: 10,
                }}
                onClick={() => {
                  setTempFilterValues(appliedFilterValues);
                  setOpenFilter(!openFilter);
                }}
              >
                <FIlterIcon />
                Filter
              </Button> */}
              {openFilter && (
                <div
                  style={{
                    position: "absolute",
                    marginTop: 10,
                    backgroundColor: "white",
                    borderRadius: "8px",
                    width: isMobile ? "280px" : "400px",
                    right: isMobile ? "" : "0px",
                    zIndex: 1000,
                    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                    marginBottom: 20,
                  }}
                >
                  <div>
                    <p
                      style={{
                        padding: 20,
                        borderBottom: "1px solid #EDF2F6",
                        color: "#01060B",
                        fontSize: "16px",
                        fontWeight: 500,
                      }}
                    >
                      Filter By Status
                    </p>
                    <div style={{ padding: "10px 20px" }}>
                      {/* Multi-select checkboxes for status */}
                      {[
                        { id: 0, name: "Draft" },
                        { id: 1, name: "Paid" },
                        { id: 2, name: "Due" },
                      ].map((option) => (
                        <div
                          key={option.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginBottom: "10px",
                          }}
                        >
                          <label
                            style={{
                              fontSize: "16px",
                              color: "#6C6F71",
                              cursor: "pointer",
                              fontWeight: 500,
                            }}
                          >
                            {option.name}
                          </label>
                          <Checkbox
                            checked={tempFilterValues.includes(option.id)}
                            onChange={(e) => {
                              let newValues;
                              if (e.target.checked) {
                                newValues = [...tempFilterValues, option.id];
                              } else {
                                newValues = tempFilterValues.filter(
                                  (id) => id !== option.id
                                );
                              }
                              setTempFilterValues(newValues);
                            }}
                            sx={{
                              color: "#2977CC",
                              "&.Mui-checked": {
                                color: "#2977CC",
                              },
                            }}
                          />
                        </div>
                      ))}
                      <div
                        style={{
                          marginTop: 10,
                          display: "flex",
                          alignItems: "center",
                          gap: 20,
                        }}
                      >
                        <Button
                          disabled={tempFilterValues.length === 0}
                          type="submit"
                          sx={{
                            width: "130px",
                            fontSize: "14px",
                            fontWeight: 600,
                            color: "#fff",
                            height: "36px",
                            background:
                              tempFilterValues.length === 0
                                ? "#BDBDBD"
                                : "linear-gradient(to right, #2977CC, #1D538F)",
                            cursor:
                              tempFilterValues.length === 0
                                ? "not-allowed"
                                : "pointer",
                            opacity: tempFilterValues.length === 0 ? 0.7 : 1,
                          }}
                          onClick={() => {
                            setFilterLoading(true);
                            setAppliedFilterValues(tempFilterValues);
                            dispatch<any>(
                              getInvoiceList({
                                offset: 0,
                                search: searchValue,
                                filter_by: tempFilterValues.length
                                  ? tempFilterValues
                                  : undefined,
                              })
                            ).finally(() => {
                              setFilterLoading(false);
                            });
                            setOpenFilter(false);
                          }}
                        >
                          Apply
                        </Button>
                        <p
                          style={{
                            fontSize: "14px",
                            fontWeight: 600,
                            background:
                              "linear-gradient(to right, #2977CC, #1D538F)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                            cursor:
                              appliedFilterValues.length === 0 &&
                              tempFilterValues.length === 0
                                ? "not-allowed"
                                : "pointer",
                            opacity:
                              appliedFilterValues.length === 0 &&
                              tempFilterValues.length === 0
                                ? 0.5
                                : 1,
                          }}
                          onClick={() => {
                            if (
                              appliedFilterValues.length === 0 &&
                              tempFilterValues.length === 0
                            )
                              return;
                            setFilterLoading(true);
                            setAppliedFilterValues([]);
                            setTempFilterValues([]);
                            dispatch<any>(
                              getInvoiceList({
                                offset: 0,
                                search: searchValue,
                                filter_by: undefined,
                              })
                            ).finally(() => {
                              setFilterLoading(false);
                            });
                            setOpenFilter(false);
                          }}
                        >
                          Reset filter
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Button
              type="submit"
              sx={{
                width: "120px",
                fontSize: "12px",
                fontWeight: 600,
                color: "#fff",
                height: "40px",
                background: createLoading ? "#BDBDBD" : "#2977CC",
                position: "relative",
                pointerEvents: createLoading ? "none" : "auto",
                opacity: createLoading ? 0.7 : 1,
              }}
              disabled={createLoading}
              onClick={() => {
                setCreateLoading(true);
                setTimeout(() => {
                  navigate("/tools/invoices/custom-invoice-form");
                  setCreateLoading(false);
                }, 600);
              }}
            >
              {createLoading ? (
                <CircularProgress size={20} sx={{ color: "#fff" }} />
              ) : (
                "Create Invoice"
              )}
            </Button>
          </div>
        </div>
      </div>
      {/**TABLE */}
      <div>
        <CustomTable
          headings={["SR NO", "client name", "Amount", "Due Date", "Action"]}
        >
          {deleting || filterLoading || searchLoading || paginationLoading ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : (
            customData?.map((item: any, index: number) => (
              <TableRow key={item.id || index}>
                <TableCell
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                  }}
                >
                  {(pageNo - 1) * limit + index + 1}
                </TableCell>
                <TableCell>
                  <Typography
                    style={{
                      fontSize: "16px",
                      fontWeight: 500,
                    }}
                  >
                    {item.client_name || item.customer_name || item.name || "-"}
                  </Typography>
                </TableCell>

                <TableCell
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                  }}
                >
                  ${item?.total_amount?.toFixed(2)}
                </TableCell>
                <TableCell
                  style={{
                    fontSize: "16px",
                    fontWeight: 500,
                  }}
                >
                  {formatDate(item.invoice_due_date) || "-"}
                </TableCell>
                <TableCell
                  style={{
                    position: isMobile || isTablet ? "relative" : "static",
                  }}
                >
                  {/**NEW options */}
                  <Box>
                    <IconButton
                      onClick={(e) => handleOpen(e, item?.customer_id)}
                      disableRipple
                      sx={{
                        padding: 1,
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                        "&:active": {
                          backgroundColor: "transparent",
                        },
                      }}
                    >
                      <MoreVertIcon />
                    </IconButton>
                    <Menu
                      key={`menu-${item.id}-${item.invoice_status}`}
                      anchorEl={anchorEl}
                      open={
                        Boolean(anchorEl) &&
                        openMenuInvoiceId === item?.customer_id
                      }
                      onClose={() => handleClose()}
                      PaperProps={{
                        sx: {
                          border: "0.5px solid #A7C7EA",
                          backgroundColor: "white",
                          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
                          borderRadius: "8px",
                          width: "165px",
                        },
                      }}
                      MenuListProps={{
                        disablePadding: true,
                      }}
                    >
                      {CUSTOM_OPTIONS.map((option, idx) => (
                        <MenuItem
                          key={option.name}
                          onClick={() => handleClose(option.name)}
                          disableRipple
                          sx={{
                            "&:hover": { backgroundColor: "transparent" },
                            "&:active": { backgroundColor: "transparent" },
                            borderBottom:
                              idx !== OPTIONS.length - 1
                                ? "1px solid #D2D7DC"
                                : "",
                            padding: "7px 16px",
                            fontWeight: 500,
                            color: "#6C6F71",
                            fontSize: "16px",
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            cursor: "pointer",
                          }}
                        >
                          {option.icon}
                          {option.name}
                        </MenuItem>
                      ))}
                    </Menu>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </CustomTable>
        {customData?.length === 0 && !searchLoading && (
          <div
            style={{
              textAlign: "center",
              marginTop: 20,
            }}
          >
            No invoice found!
          </div>
        )}
        {/* Pagination */}
        {Math.ceil(total_records / limit) > 1 && (
          <Box sx={{ display: "flex", justifyContent: "end", mt: 3 }}>
            <CommonPagination
              currentPage={pageNo}
              totalPages={Math.ceil(total_records / limit)}
              onPageChange={handlePageChange}
            />
          </Box>
        )}
      </div>
      <Modal open={deleteModalOpen} onClose={handleDeleteCancel}>
        <ModalWrapper
          sx={{
            width: isMobile ? "350px" : "500px",
            p: 0,
            height: "370px",
            backgroundImage: "none",
            position: "relative",
            borderRadius: "20px",
            overflow: "hidden",
            outline: "none",
          }}
        >
          {/* Top right decoration */}
          <div
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              zIndex: 1,
            }}
          >
            <ModalTop />
          </div>
          {/* Bottom left decoration */}
          <div
            style={{
              position: "absolute",
              bottom: -6,
              left: 0,
              zIndex: 1,
            }}
          >
            <ModalBottom />
          </div>
          {/* Ensure close button is above decorations */}
          <CloseButtonWrapper
            sx={{ zIndex: 10, position: "relative", marginTop: 1 }}
          >
            <CloseButton onClick={handleDeleteCancel}>
              <CrossIcon />
            </CloseButton>
          </CloseButtonWrapper>
          <Box sx={{ p: 4, pt: 0 }}>
            {/* Trash icon with red bg */}
            <Box
              sx={{ display: "flex", justifyContent: "center", mt: 1, mb: 2 }}
            >
              <Box
                sx={{
                  background: "#F20F0014",
                  borderRadius: "50%",
                  p: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TrashIconForPopup />
              </Box>
            </Box>
            <div
              style={{
                textAlign: "center",
                fontSize: isMobile ? "20px" : "24px",
                color: "#01060B",
                fontWeight: 700,
                marginTop: 0,
                marginBottom: 10,
              }}
            >
              Delete Invoice
            </div>
            <div
              style={{
                width: isMobile ? "100%" : "250px",
                textAlign: "center",
                lineHeight: "24px",
                margin: "0 auto",
                fontSize: isMobile ? "12px" : "16px",
                color: "#6C6F71",
                fontWeight: 500,
                fontFamily: "Plus Jakarta Sans",
              }}
            >
              Are you sure you want to delete this invoice?
            </div>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                mt: 3,
                justifyContent: "center",
              }}
            >
              <Button
                sx={{
                  border: "2px solid #2977CC",
                  borderRadius: "8px",
                  width: "192px",
                  height: "51px",
                  fontSize: "16px",
                  fontWeight: 600,
                  color: "#2977CC",
                }}
                onClick={handleDeleteCancel}
              >
                Close
              </Button>
              <Button
                sx={{
                  background: "linear-gradient(to right, #2977CC, #1D538F)",
                  color: "white",
                  width: "192px",
                  height: "51px",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
                onClick={handleDeleteConfirm}
              >
                Yes
              </Button>
            </Box>
          </Box>
        </ModalWrapper>
      </Modal>
    </div>
  );
};

export default CustomInvoiceTable;
